const STREAMING_STATUSES = new Set(['queued', 'pending', 'running', 'in_progress', 'streaming']);

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

const getCallId = (m) =>
  isNonEmptyString(m?.callId) ? m.callId : isNonEmptyString(m?.call_id) ? m.call_id : null;

const getMessageType = (m) => (isNonEmptyString(m?.type) ? m.type.toLowerCase() : null);

const safeJsonParse = (raw) => {
  if (!isNonEmptyString(raw)) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const startCase = (value = '') =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const humanizeComponentId = (id) => startCase(String(id || '').replace(/[-_]+/g, ' '));

const extractTextFromAssistantContent = (content) => {
  if (Array.isArray(content)) {
    return content
      .map((part) =>
        isNonEmptyString(part?.text)
          ? part.text
          : isNonEmptyString(part?.content)
            ? part.content
            : ''
      )
      .filter(Boolean)
      .join('\n\n')
      .trim();
  }

  // Sometimes assistant content might be string
  if (isNonEmptyString(content)) return content.trim();

  return '';
};

const extractMessageText = (message) => {
  // user messages in response are: { content: "...", role: "user" }
  if (isNonEmptyString(message?.content)) return message.content.trim();

  // assistant messages are: { content: [...], role: "assistant", type: "message" }
  const fromAssistantParts = extractTextFromAssistantContent(message?.content);
  if (fromAssistantParts) return fromAssistantParts;

  // function_call_output messages usually use "output"
  if (isNonEmptyString(message?.output)) return message.output.trim();

  return '';
};

const normalizeRole = (message) => {
  const rawRole = isNonEmptyString(message?.role) ? message.role.toLowerCase() : null;
  if (rawRole === 'user' || rawRole === 'assistant') return rawRole;

  // Tool call "message" rows should be treated as assistant
  const type = getMessageType(message);
  if (type === 'message') return 'assistant';

  return null;
};

const resolveMessageId = (message, role, index, createFallbackId) => {
  const explicitId =
    message?.id ?? message?.message_id ?? message?.messageId ?? message?.uuid ?? null;

  if (explicitId != null) return String(explicitId);

  const callId = getCallId(message);
  if (role === 'assistant' && callId) return `assistant-call-${callId}`;

  return createFallbackId ? createFallbackId(role) : `${role}-${index}`;
};

const DEFAULT_VISUALIZATION_TYPE = 'table';

const COMPONENT_DRAFT_CONFIG = {
  'brand-defense-brand-exclusivity': {
    title:
      'How do {product} shoppers{store} compare to other {category} shoppers (buy {category} but not {product}){store} demographically?',
    type: 'table',
  },
  'brand-defense-category-brand-performance': {
    title:
      'How is {product} doing in context with other {category} products{place} in terms of {metric} penetration and buy rate?',
    type: 'table',
  },
  'brand-defense-category-conversion': {
    title:
      'How do {product} shoppers{store} compare to other {category} shoppers (buy {category} but not {product}){store} demographically?',
    type: 'table',
  },
  'brand-defense-competitive-basket': {
    title: 'Does {product} drive competitive basket spend versus other {category} products{store}?',
    type: 'scatter-chart',
  },
  'brand-defense-shopper-conversion': {
    title: 'How well do top retailers convert {product} {category} shoppers at their stores?',
    type: 'bar-chart',
  },
  'brand-defense-shopper-profile': {
    title:
      'How do {product} shoppers{store} compare to other {category} shoppers (buy {category} but not {product}){store} demographically?',
    type: 'table',
  },
  'brand-defense-shopper-value': {
    title:
      'How valuable are {product} shoppers to the total {category} category vs shoppers of other products, based on buy rate and purchase frequency?',
    type: 'table',
  },
  'brand-sell-in-category-conversion': {
    title: 'How well does {product} convert shoppers into the {category} category across stores?',
    type: 'bar-stack-chart',
  },
  'brand-sell-in-category-leakage': {
    title: 'How well does {store} convert total {category} category shoppers at their stores?',
    type: 'bar-chart-two-labels',
  },
  'brand-sell-in-category-leakage-to-brand-elsewhere': {
    title:
      'To what extent and where do non-converted {category} category shoppers at {store} go to purchase {product}?',
    type: 'bar-chart',
  },
  'brand-sell-in-consecutive-repeat-rate': {
    title:
      'When {product} shoppers make their next trip to the {category} category, are they returning to {product}?',
    type: 'bar-chart-two-labels',
  },
  'brand-sell-in-metric-tree': {
    title:
      'How did panel metrics impact the YOY change in {product} spend (anywhere, not specific to {store})?',
    type: 'brand-tree-v2',
  },
  'brand-sell-in-shopper-profile': {
    title:
      'How do {product} shoppers (anywhere) demographically compare to current {category} category shoppers at {store}?',
    type: 'table',
  },
  'brand-sell-in-shopper-value': {
    title:
      "How valuable are {product} shoppers (who buy it at any store) to the total {category} category and total store specifically at {store} (even though they don't buy {product} there) vs shoppers of competitor products?",
    type: 'table',
  },
  'brand-sell-in-source-of-volume': {
    title: "How incremental has {focus_brand}'s growth been to the {focus_category} category?",
    type: 'ebsova-tree',
  },
  'category-review-category-brand-performance': {
    title: 'How are top brands in {category} doing in terms of household penetration and buy rate?',
    type: 'table',
  },
  'category-review-channel-drivers': {
    title: 'Where do shoppers buy {product}, by {attribute}?',
    type: 'column-chart',
  },
  'category-review-dev-index-age-generation': {
    title: 'Which generations contribute more than expected to {category} category spend{place}?',
    type: 'table',
  },
  'category-review-dev-index-regional': {
    title: 'Which {attribute}s contribute more than expected to {category} category spend{place}?',
    type: 'table',
  },
  'category-review-lrn': {
    title: 'What is the extent of Lapsed, Retained, and New shoppers {product}{store}?',
    type: 'column-chart',
  },
  'category-review-metric-tree': {
    title: 'How did panel metrics impact the YOY change in {product} spend{place}?',
    type: 'brand-tree-v2',
  },
  'category-review-place-drivers': {
    title: 'Where do shoppers buy {product}, by {attribute}?',
    type: 'column-chart',
  },
  'category-review-retailer-drivers': {
    title: 'Where do shoppers buy {product}, by {attribute}?',
    type: 'column-chart',
  },
  'category-review-seasonality': {
    title: 'How is {category} spend distributed by month{place}?',
    type: 'column-chart',
  },
  'category-review-shopper-profile': {
    title: 'How do {place}{category} shoppers compare demographically to all {place}shoppers?',
    type: 'table',
  },
  'diagnosing-growth-category-conversion-among-new': {
    title:
      'To what extent were new {product} shoppers{place} also new to the {category} category{place}?',
    type: 'bar-stack-chart',
  },
  'diagnosing-growth-category-retention-after-lapsing': {
    title:
      'To what extent did lapsed {product} shoppers{place} continue buying other {category} products{place}?',
    type: 'bar-stack-chart',
  },
  'diagnosing-growth-lrn': {
    title: 'What is the extent of Lapsed, Retained, and New shoppers {product}{store}?',
    type: 'column-chart',
  },
  'diagnosing-growth-metric-tree': {
    title: 'How did panel metrics impact the YOY change in {product} spend{place}?',
    type: 'brand-tree-v2',
  },
  'diagnosing-growth-source-of-volume': {
    title: 'What was the source of volume for {product} in the {category}{place}?',
    type: 'ebsova-tree',
  },
  'diagnosing-growth-yoy-changes-product-vs-category': {
    title:
      '{place} did the year-over-year KPI changes for {product} in {category} compare to those for the rest of {category}?',
    type: 'table',
  },
  'diagnosing-growth-yoy-metrics-by-demographics': {
    title:
      '{place} did the year-over-year KPI changes for {product} in {category} compare across demographics?',
    type: 'table',
  },
  'diagnosing-growth-yoy-metrics-retained-shoppers': {
    title:
      'How did key purchasing metrics change year over year among retained shoppers of {product} {category}{place}?',
    type: 'table',
  },
  'incrementality-basket-value': {
    title: 'Do baskets with {product} drive more value than other {category} baskets?',
    type: 'table',
  },
  'incrementality-brand-exclusivity': {
    title:
      'How well does {product} drive exclusivity in {category}{store}, based on product shoppers?',
    type: 'table',
  },
  'incrementality-brand-exclusivity-among-category': {
    title:
      'How well does {product} drive exclusivity in {category}{store}, based on category shoppers?',
    type: 'bar-chart-two-labels',
  },
  'incrementality-category-conversion': {
    title: 'How well does {product} convert shoppers into the {category} category{place}?',
    type: 'bar-stack-chart',
  },
  'incrementality-overlapping-spend': {
    title:
      '{place} shoppers buying both {product} and other {category} products drive higher spend than those buying only one or the other?',
    type: 'table',
  },
  'incrementality-poe': {
    title:
      '{place} shoppers entering the {category} category through {product} more or less than expected?',
    type: 'table',
  },
  'incrementality-share-of-category-requirements': {
    title: 'How loyal are {product} shoppers within {category}?',
    type: 'table',
  },
  'incrementality-source-of-volume': {
    title: "{place} incremental has {product}'s growth been to the {category} category?",
    type: 'incrementality-source-of-volume',
  },
  'who-is-my-shopper-banner-drivers': {
    title: 'Where do shoppers buy {product}, by {attribute}?',
    type: 'column-chart',
  },
  'who-is-my-shopper-category-conversion': {
    title: 'How well does {product} convert shoppers into the {category} category?',
    type: 'bar-stack-chart',
  },
  'who-is-my-shopper-channel-drivers': {
    title: 'Where do shoppers buy {product}, by {attribute}?',
    type: 'column-chart',
  },
  'who-is-my-shopper-cross-shopping': {
    title: '{place} are {product} shoppers interacting with other products in {category}?',
    type: 'table',
  },
  'who-is-my-shopper-demo-differences': {
    title: 'How do key demographics differ in their spending on {product} {category}{place}?',
    type: 'table',
  },
  'who-is-my-shopper-demo-shifts': {
    title:
      'Which demographics have growing/declining household penetration for {product} compared to the total {category} category?',
    type: 'table',
  },
  'who-is-my-shopper-metrics-tree': {
    title: 'How did panel metrics impact the YOY change in {product} spend{place}?',
    type: 'brand-tree-v2',
  },
  'who-is-my-shopper-place-drivers': {
    title: 'Where do shoppers buy {product}, by {attribute}?',
    type: 'column-chart',
  },
  'who-is-my-shopper-shopper-profile': {
    title:
      'How do{place} {product} {category} shoppers demographically compare to total{place} {category} shoppers?',
    type: 'table',
  },
};

const resolveVisualizationType = (rawType) => {
  if (!isNonEmptyString(rawType)) return DEFAULT_VISUALIZATION_TYPE;
  const trimmed = rawType.trim();
  if (!trimmed || trimmed === '-' || trimmed.toLowerCase() === 'none') {
    return DEFAULT_VISUALIZATION_TYPE;
  }
  return trimmed;
};

const normalizeFlowState = (rawFlowState) => {
  if (!rawFlowState || typeof rawFlowState !== 'object') return null;

  const camelOrSnake = (obj, camel, snake, fallback) => {
    if (!obj) return fallback;
    if (obj[camel] !== undefined) return obj[camel];
    if (obj[snake] !== undefined) return obj[snake];
    return fallback;
  };

  const componentsRaw = camelOrSnake(rawFlowState, 'components', 'components', []);
  const components = Array.isArray(componentsRaw)
    ? componentsRaw.map((c) => (isNonEmptyString(c) ? c : null)).filter(Boolean)
    : [];

  return {
    businessQuestion: camelOrSnake(rawFlowState, 'businessQuestion', 'business_question', ''),
    components,
    dateRangeEnd: camelOrSnake(rawFlowState, 'dateRangeEnd', 'date_range_end', null),
    dateRangeStart: camelOrSnake(rawFlowState, 'dateRangeStart', 'date_range_start', null),
    editablePromptAnswers: camelOrSnake(
      rawFlowState,
      'editablePromptAnswers',
      'editable_prompt_answers',
      {}
    ),
    editablePromptRequirements: camelOrSnake(
      rawFlowState,
      'editablePromptRequirements',
      'editable_prompt_requirements',
      []
    ),
    readOnlyPromptAnswers: camelOrSnake(
      rawFlowState,
      'readOnlyPromptAnswers',
      'read_only_prompt_answers',
      {}
    ),
    readOnlyPromptRequirements: camelOrSnake(
      rawFlowState,
      'readOnlyPromptRequirements',
      'read_only_prompt_requirements',
      []
    ),
  };
};

export const mapFlowStateComponentsToDrafts = (rawFlowState) => {
  const normalized = normalizeFlowState(rawFlowState);
  if (!normalized) return [];

  return normalized.components.map((componentId, index) => {
    const config = COMPONENT_DRAFT_CONFIG[componentId] || {};
    const type = resolveVisualizationType(config.type);

    return {
      componentId,
      dataset: {
        type,
      },
      id: `draft-${componentId}-${index}`,
      title: config.title || humanizeComponentId(componentId),
      visualizationType: type,
    };
  });
};

export const extractChatFlowState = (rawFlowState) => normalizeFlowState(rawFlowState);

const parsePromptOptionList = (rawOutput) => {
  return rawOutput;
};

const formatToolOutputToAssistantMessages = ({
  createFallbackId,
  index,
  message,
  toolMetadataEntry,
}) => {
  const toolName = toolMetadataEntry?.name;
  const rawOutput = isNonEmptyString(message?.output) ? message.output : '';

  // const baseId = resolveMessageId(message, 'assistant', index, createFallbackId);

  if (toolName === 'retrieve_component_descriptions') {
    // BI-21593: This tool call is internal to the agent for now.
    return [];
  }

  if (toolName === 'update_components') {
    // BI-21593: Don't show answers like TCC version to the user.
    // This tool call is internal to the agent for now.
    // In the future this may need to trigger re-rendering titles of draft cards potentially.
    return [];
  }

  if (toolName === 'prompt_search') {
    // return [
    //   buildAssistantMessage({
    //     content: `Searching options…`,
    //     id: `${baseId}-prompt-search`,
    //     variant: 'status',
    //   }),
    // ].filter(Boolean);

    // Default: ignore
    parsePromptOptionList(rawOutput);
    return [];
  }

  return [];
};

/**
 * @param {any} payload
 * @param {{createFallbackId?: (role: string) => string}} options
 * @return {{flowId: number|null, flowState: Object|null, messages: Array<{id: string, role: string, content: string, isStreaming?: boolean}>}}
 */
export const transformChatSessionData = (payload, options = {}) => {
  const { createFallbackId } = options;

  const messages = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.messages)
      ? payload.messages
      : Array.isArray(payload?.data?.messages)
        ? payload.data.messages
        : [];

  const toolMetadataByCallId = new Map();
  messages.forEach((m) => {
    if (getMessageType(m) !== 'function_call') return;
    const callId = getCallId(m);
    if (callId) toolMetadataByCallId.set(callId, m);
  });

  const results = [];
  let kickoffFlowId = null;

  messages.forEach((message, index) => {
    const type = getMessageType(message);

    if (type === 'function_call_kickoff_output') {
      const rawOutput = message?.output;
      if (typeof rawOutput === 'object' && rawOutput !== null) {
        kickoffFlowId = rawOutput?.flowId ?? rawOutput?.flow_id ?? kickoffFlowId;
      } else if (typeof rawOutput === 'string') {
        const parsed = safeJsonParse(rawOutput);
        if (parsed?.flowId != null || parsed?.flow_id != null) {
          kickoffFlowId = parsed?.flowId ?? parsed?.flow_id ?? kickoffFlowId;
        } else if (kickoffFlowId == null) {
          const numericFallback = Number(rawOutput);
          if (Number.isFinite(numericFallback)) {
            kickoffFlowId = numericFallback;
          }
        }
      }
      return;
    }

    if (type === 'function_call') return;

    if (type === 'function_call_output') {
      const callId = getCallId(message);
      if (!callId) return;

      const toolMetadataEntry = toolMetadataByCallId.get(callId);
      if (!toolMetadataEntry) return;

      const derived = formatToolOutputToAssistantMessages({
        createFallbackId,
        index,
        message,
        toolMetadataEntry,
      });

      if (derived.length) results.push(...derived);
      return;
    }

    const role = normalizeRole(message);
    if (!role) return;

    const text = extractMessageText(message);
    if (!isNonEmptyString(text)) return;

    const status = isNonEmptyString(message?.status) ? message.status.toLowerCase() : null;

    results.push({
      content: text,
      id: resolveMessageId(message, role, index, createFallbackId),
      isStreaming:
        Boolean(message?.isStreaming ?? message?.is_streaming) ||
        (status ? STREAMING_STATUSES.has(status) : false) ||
        type === 'streaming',
      role,
    });
  });

  const flowState = extractChatFlowState(payload?.flowState || payload?.flow_state);

  return {
    flowId: kickoffFlowId,
    flowState,
    messages: results,
  };
};
